'use client';

import { Flex, FormLabel, Link, NumberStepInput } from '@coolblue-development/freddy';
import { default as productTypeOpenProductSummaryTrigger } from '@coolblue-development/ga4-events/triggers/ProductTypeSection/openProductSummaryTrigger';
import { default as productTypeSelectEnergyLabelInformationTrigger } from '@coolblue-development/ga4-events/triggers/ProductTypeSection/selectEnergyLabelInformationTrigger';
import { default as productTypeShowEnergyLabelTrigger } from '@coolblue-development/ga4-events/triggers/ProductTypeSection/showEnergyLabelTrigger';
import { default as recommendedClickReviewsOfShownProductTrigger } from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/clickReviewsOfShownProductTrigger';
import { default as recommendedOpenProductSummaryTrigger } from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/openProductSummaryTrigger';
import { default as recommendedSelectEnergyLabelInformationTrigger } from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/selectEnergyLabelInformationTrigger';
import { default as recommendedShowEnergyLabelTrigger } from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/showEnergyLabelTrigger';
import viewReviewPanelTrigger from '@coolblue-development/ga4-events/triggers/ReviewPanel/viewReviewPanelTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { CoolbluesChoiceLabel } from '@coolblue-development/next-coolblues-choice-label/client';
import EnergyLabel from '@coolblue-development/next-energy-label/client';
import { MiniProductPage } from '@coolblue-development/next-mini-product-page/client';
import { ColorAlternative, ProductTitle } from '@coolblue-development/next-patterns/client';
import { PromotionalProductImage } from '@coolblue-development/next-patterns/core';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { ReviewsDrawer } from '@coolblue-development/next-reviews/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import { useAccessoryProductCardContext } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import type { ATCPageAutomatedSectionSlot } from '@features/add-to-cart-page/typings/AutomatedSection';
import type {
  AlternativeProduct,
  HighlightedAlternative,
  Product
} from '@features/add-to-cart-page/typings/Product';
import type { ATCPageProductTypeSectionSlot } from '@features/add-to-cart-page/typings/ProductTypeSection';
import legacyPromotionalContentFactory from '@features/shared/PromotionalContent/legacyPromotionalContentFactory';
import { promotionalContentFactory } from '@features/shared/PromotionalContent/promotionalContentFactory';
import { useTranslation } from '@i18n/useTranslation';
import { ForwardedRef, ReactElement, useEffect, useRef, useState } from 'react';

import AccessoryProductCard from '../AccessoryProductCard/AccessoryProductCard';
import { useCustomerContext } from '../CustomerContext/CustomerContext';
export default function ProductCard({
  product,
  slot,
  mainProductId,
  accessoryProductCardIndex,
  accessoryProductCardTotalIndices,
  highlightedAlternative,
  isAutomated,
  showPromoLabelPlaceholder,
  shouldUsePromotionalContent
}: {
  product: Product;
  slot: ATCPageProductTypeSectionSlot | ATCPageAutomatedSectionSlot;
  mainProductId: number;
  accessoryProductCardIndex: number;
  accessoryProductCardTotalIndices: number;
  highlightedAlternative?: HighlightedAlternative;
  isAutomated: boolean;
  showPromoLabelPlaceholder: boolean;
  shouldUsePromotionalContent: boolean;
}): ReactElement {
  const { t } = useTranslation('webshop');
  const context = useAccessoryProductCardContext();
  const { language } = useRequestContext();
  const ctaRef: ForwardedRef<HTMLButtonElement | null> = useRef(null);
  const { trackEvent } = useAnalytics();
  const { isBusinessCustomer } = useCustomerContext();

  const [isReviewsDrawerOpen, setIsReviewsDrawerOpen] = useState(false);
  const [isMiniProductPageOpen, setIsMiniProductPageOpen] = useState(false);

  const displayData: AlternativeProduct =
    context.productAlternatives[context.selectedProductAlternative];

  const cbcLabel = displayData.coolbluesChoice?.content.type ? (
    <CoolbluesChoiceLabel
      variant={displayData.coolbluesChoice.content.type === 'Green' ? 'green' : undefined}
      size='small'
    />
  ) : undefined;

  const colorAlternatives: ColorAlternative[] = context.sanitizeColorAlternativesData(product);

  const onSelectColorAlternative = (index: string) => {
    if (product.colorAlternatives?.[+index]?.product) {
      context.setSelectedProductAlternative(
        (product.colorAlternatives[+index].product as Product).id
      );
      context.updateSelectedColorAlternatives(product.id, +index);
    }
  };

  const productType =
    product.productType?.singularName && product.productType?.singularName.length > 1
      ? product.productType.singularName.charAt(0).toUpperCase() +
        product.productType.singularName.slice(1)
      : product.productType?.singularName;

  useEffect(() => {
    context.updateProductAlternatives({ highlightedAlternative });
    // Ignoring dependency list to ensure single run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMiniProductPageOpen = () => {
    setIsMiniProductPageOpen(true);
    if (isAutomated) {
      trackEvent(recommendedOpenProductSummaryTrigger({ sub_feature: 'recommended_accessories' }));
    } else {
      trackEvent(productTypeOpenProductSummaryTrigger({ sub_feature: 'product_type_section' }));
    }
  };

  const handleMiniProductPageClose = () => {
    setIsMiniProductPageOpen(false);
  };

  function trackSelectEnergyLabelInformation() {
    if (isAutomated) {
      trackEvent(
        recommendedSelectEnergyLabelInformationTrigger({ content: 'product_information_sheet' })
      );
    } else {
      trackEvent(
        productTypeSelectEnergyLabelInformationTrigger({ content: 'product_information_sheet' })
      );
    }
  }

  const salesPriceComponent = displayData.salesPrice ? (
    <SalesPrice
      variant={isBusinessCustomer ? 'business' : undefined}
      personalPrice={displayData.personalPrice}
      currentPrice={
        <SalesPriceCurrent
          price={displayData.salesPrice.includingVat}
          showPricePerItem={isBusinessCustomer}
          priceExcludingVat={isBusinessCustomer ? displayData.salesPrice.excludingVat : undefined}
        />
      }
      {...(displayData.listPrice?.includingVat && {
        formerPrice: (
          <SalesPriceFormer
            brandName={displayData.brand?.name}
            price={displayData.listPrice.includingVat}
            prefix={t('orderBlock.salesPrice.prefix')}
          />
        )
      })}
    />
  ) : null;

  const titleReviewComponent = (
    <ProductTitle.Review
      value={displayData.reviewMetaData?.averageRating || 0}
      reviewCount={displayData.reviewMetaData?.count}
    />
  );

  const legacyPromotionalContent = legacyPromotionalContentFactory(product.promoIcon, product.name);

  return (
    <AccessoryProductCard
      ref={ctaRef}
      productTitle={
        <ProductTitle>
          <ProductTitle.Eyebrow>{productType}</ProductTitle.Eyebrow>
          <ProductTitle.Title>
            <MiniProductPage
              productId={displayData.id}
              mainProductId={mainProductId}
              index={accessoryProductCardIndex}
              totalIndices={accessoryProductCardTotalIndices}
              ctaRef={ctaRef}
              onOpen={handleMiniProductPageOpen}
              onClose={handleMiniProductPageClose}
              onSelectColorAlternative={
                // Only display color alternatives for main product
                colorAlternatives.findIndex(item => item.productId === displayData.id) > -1
                  ? value => {
                      onSelectColorAlternative(value);
                    }
                  : undefined
              }
              colorPickerInitialProductId={product.id}
              isBusinessCustomer={isBusinessCustomer}
              quantity={
                <Flex alignItems='center' gap={200} flex={1}>
                  <FormLabel> {t('addToCart.quantity')}</FormLabel>
                  <NumberStepInput
                    value={context.selectedProductQuantity || 1}
                    min={1}
                    onChange={quantity =>
                      context.setSelectedProductQuantity(quantity, product?.orderQuantityLimit)
                    }
                    aria-label={t('addToCart.productQuantity', { productName: product.name })}
                    max={product.orderQuantityLimit || undefined}
                  />
                </Flex>
              }
            >
              {displayData.name}
            </MiniProductPage>
          </ProductTitle.Title>
          {!!displayData.reviewMetaData?.count && displayData.reviewMetaData.count > 0 ? (
            <>
              <Link
                component='button'
                onClick={() => {
                  setIsReviewsDrawerOpen(true);
                  trackEvent(viewReviewPanelTrigger());
                  trackEvent(
                    recommendedClickReviewsOfShownProductTrigger({
                      sub_feature: 'recommended_accessories',
                      item_id: displayData.id
                    })
                  );
                }}
              >
                {titleReviewComponent}
              </Link>
              {isReviewsDrawerOpen && (
                <ReviewsDrawer
                  productInfo={{
                    fullName: displayData.name,
                    url: displayData.urls?.product || '',
                    id: displayData.id,
                    imageId: displayData.primaryImage?.id || ''
                  }}
                  lang={language}
                  isOpen={isReviewsDrawerOpen}
                  setIsOpen={setIsReviewsDrawerOpen}
                  salesPriceComponent={salesPriceComponent}
                />
              )}
            </>
          ) : (
            titleReviewComponent
          )}
        </ProductTitle>
      }
      salesPrice={salesPriceComponent}
      promotionalProductImage={
        <PromotionalProductImage
          alt={displayData.name}
          imageId={displayData.primaryImage?.id ?? ''}
          primaryLabel={cbcLabel}
          promotionalContent={
            shouldUsePromotionalContent
              ? promotionalContentFactory(product.promotionalContent)
              : legacyPromotionalContent
          }
          showPromoLabelPlaceholder={showPromoLabelPlaceholder}
        />
      }
      description={
        (isAutomated
          ? displayData.prosAndCons?.pros?.[0]
          : (slot as ATCPageProductTypeSectionSlot).description ||
            displayData.prosAndCons?.pros?.[0]) || t('addToCart.defaultDescription')
      }
      energyLabel={
        displayData.energyLabel && (
          <EnergyLabel
            direction='right'
            imageId={Number(displayData.energyLabel.image?.id)}
            level={displayData.energyLabel.level}
            size='medium'
            version={displayData.energyLabel.version}
            specificationUrl={displayData.energyLabel.specification?.absoluteUrl}
            onContentClick={trackSelectEnergyLabelInformation}
            onShow={() =>
              isAutomated
                ? recommendedShowEnergyLabelTrigger()
                : productTypeShowEnergyLabelTrigger()
            }
          />
        )
      }
      hasAlternatives={true} // The alternatives link is always displayed
      product={displayData}
      mainProductId={mainProductId}
      productTypeNameSingular={product.productType?.singularName}
      productTypeNamePlural={product.productType?.pluralName}
      productTypeId={Number(slot.productTypeId)}
      classificationId={'classificationId' in slot ? slot.classificationId : undefined}
      accessoryProductCardIndex={accessoryProductCardIndex}
      accessoryProductCardTotalIndices={accessoryProductCardTotalIndices}
      highlightedAlternative={highlightedAlternative}
      isHighlighted={displayData.isHighlighted}
      isAutomated={isAutomated}
      colorAlternatives={colorAlternatives}
      onSelectColorAlternative={onSelectColorAlternative}
      isMiniProductPageOpen={isMiniProductPageOpen}
    />
  );
}
